import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Body1, Button, Caption1, makeStyles, tokens } from '@fluentui/react-components';
import {
  WeatherSunnyRegular,
  PulseRegular,
  AlertRegular,
  GlobeRegular,
  PeopleRegular,
  HeartPulseRegular,
  ArrowSwapRegular,
  ClockRegular,
  BeakerRegular,
  LiveRegular,
  CircleRegular,
  type FluentIcon,
} from '@fluentui/react-icons';
import { RagBadge } from './RagBadge';
import { corroborates } from './corroboration';
import type { BoardSignal } from '../../../../data/roleboard/occupancy-data';
import { space, radii, motion } from '../../../../theme/design-system';
import { ragColors } from '../../../../theme/curavias-theme';

const SIGNAL_ICONS: Record<string, FluentIcon> = {
  weather: WeatherSunnyRegular,
  pulse: PulseRegular,
  alert: AlertRegular,
  seismic: GlobeRegular,
  globe: GlobeRegular,
  people: PeopleRegular,
  heartpulse: HeartPulseRegular,
  swap: ArrowSwapRegular,
  clock: ClockRegular,
};

const useStyles = makeStyles({
  wrap: { display: 'flex', flexDirection: 'column', gap: space.l },
  section: { display: 'flex', flexDirection: 'column', gap: space.xs },
  sectionHead: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: space.s,
    paddingTop: space.s,
    paddingBottom: space.s,
    paddingLeft: space.m,
    paddingRight: space.m,
    borderRadius: radii.control,
    backgroundColor: tokens.colorNeutralBackground2,
    transitionProperty: 'background-color',
    transitionDuration: motion.durationFast,
    ':hover': { backgroundColor: tokens.colorNeutralBackground2Hover },
  },
  leadIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '20px',
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  main: { flexGrow: 1, minWidth: 0 },
  detail: { color: tokens.colorNeutralForeground3 },
  badges: { display: 'flex', alignItems: 'center', gap: space.s, flexShrink: 0 },
  provIcon: { display: 'inline-flex', alignItems: 'center', fontSize: '18px' },
  trustChip: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.02em',
    paddingLeft: space.xs,
    paddingRight: space.xs,
    borderRadius: radii.control,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  trustChipB: {
    color: tokens.colorBrandForeground2,
    backgroundColor: tokens.colorBrandBackground2,
  },
  webCitations: {
    listStyleType: 'none',
    margin: 0,
    paddingLeft: 0,
    marginTop: space.xs,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  webCiteLink: { fontSize: '12px', color: tokens.colorBrandForegroundLink },
  corroboratedChip: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    paddingLeft: space.xs,
    paddingRight: space.xs,
    borderRadius: radii.control,
    color: tokens.colorPaletteGreenForeground2,
    backgroundColor: tokens.colorPaletteGreenBackground2,
  },
  onWatchChip: {
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    paddingLeft: space.xs,
    paddingRight: space.xs,
    borderRadius: radii.control,
    color: tokens.colorBrandForeground2,
    backgroundColor: tokens.colorBrandBackground2,
  },
});

interface SignalsPanelProps {
  signals: BoardSignal[];
  /** HITL: promote an advisory (Trust-B) signal to the CSA watch queue. Advisory only. */
  onPromoteToWatch?: (signalId: string) => void;
}

/**
 * Sprint 27 — OOA Signals panel: external Trust-A feeds + internal operational
 * feeds. Each row is [signal icon] name · detail — [RAG status badge] [provenance
 * icon]. Provenance is icon-only (beaker = simulated, live = live) with an
 * accessible label. All demo rows are `simulated` (ADR-0016); a row flips to
 * `live` when a real adapter binds.
 */
export function SignalsPanel({ signals, onPromoteToWatch = () => {} }: SignalsPanelProps) {
  const s = useStyles();
  const { t } = useTranslation();
  const [promoted, setPromoted] = useState<ReadonlySet<string>>(new Set());
  const external = signals.filter((x) => x.scope === 'external');
  const internal = signals.filter((x) => x.scope === 'internal');

  // Display-only: a Trust-A row is "corroborated" when a Trust-B web signal in
  // the same panel shares its hazard + canton (ADR-0060). Never arms a lever.
  const webSignals = external.filter((x) => x.trustClass === 'Trust-B' && x.hazardType && x.cantons);
  const corroboratedIds = new Set(
    external
      .filter((a) => a.trustClass === 'Trust-A' && a.hazardType && a.cantons)
      .filter((a) =>
        webSignals.some((w) =>
          corroborates(
            { hazardType: a.hazardType!, cantons: a.cantons!, trustClass: 'Trust-A' },
            { hazardType: w.hazardType!, cantons: w.cantons!, trustClass: 'Trust-B' },
          ),
        ),
      )
      .map((a) => a.id),
  );

  const renderRow = (sig: BoardSignal) => {
    const LeadIcon = SIGNAL_ICONS[sig.iconKey] ?? CircleRegular;
    const isLive = sig.provenance === 'live';
    const ProvIcon = isLive ? LiveRegular : BeakerRegular;
    const provLabel = isLive ? t('badge.liveData') : t('badge.simulatedData');
    return (
      <div key={sig.id} className={s.row}>
        <span className={s.leadIcon} aria-hidden="true"><LeadIcon /></span>
        <div className={s.main}>
          <Body1>
            {sig.label}
            {sig.detail ? <span className={s.detail}>{` \u00b7 ${sig.detail}`}</span> : null}
          </Body1>
          {sig.webCitations?.length ? (
            <ul className={s.webCitations}>
              {sig.webCitations.map((c) => (
                <li key={c.uri}>
                  <a className={s.webCiteLink} href={c.uri} target="_blank" rel="noreferrer">{c.title}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className={s.badges}>
          {sig.trustClass ? (
            <span className={`${s.trustChip} ${sig.trustClass === 'Trust-B' ? s.trustChipB : ''}`}>
              {sig.trustClass}
            </span>
          ) : null}
          {corroboratedIds.has(sig.id) ? (
            <span className={s.corroboratedChip}>
              {t('ooa.signals.corroborated', 'Corroborated')}
            </span>
          ) : null}
          {sig.trustClass === 'Trust-B' ? (
            promoted.has(sig.id) ? (
              <span className={s.onWatchChip}>{t('ooa.signals.onWatch', 'On CSA watch')}</span>
            ) : (
              <Button
                size="small"
                appearance="subtle"
                onClick={() => {
                  setPromoted((prev) => new Set(prev).add(sig.id));
                  onPromoteToWatch(sig.id);
                }}
              >
                {t('ooa.signals.promoteToWatch', 'Promote to watch')}
              </Button>
            )
          ) : null}
          <RagBadge tone={sig.statusTone}>{sig.statusLabel}</RagBadge>
          <span
            className={s.provIcon}
            role="img"
            aria-label={provLabel}
            title={provLabel}
            style={{ color: isLive ? ragColors.good : ragColors.neutral }}
          >
            <ProvIcon />
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className={s.wrap} data-testid="ooa-signals-panel">
      <div className={s.section}>
        <Caption1 className={s.sectionHead}>
          {t('ooa.signals.external', 'External signals')}
        </Caption1>
        {external.map(renderRow)}
      </div>
      <div className={s.section}>
        <Caption1 className={s.sectionHead}>{t('ooa.signals.internal', 'Internal signals')}</Caption1>
        {internal.map(renderRow)}
      </div>
    </div>
  );
}
